if (!customElements.get("anchor-nav")) {
  customElements.define(
    "anchor-nav",
    class AnchorNav extends HTMLElement {
      constructor() {
        super();

        this.intersectionObserver = null;

        // Get all anchor targets
        this.getAnchors();
        if (this.anchors.length <= 0) return;

        // Initially set the first anchor as active
        this.setAnchorActive(this.anchors[0]["element"]);

        // Listen for anchor clicks and scroll to the corresponding position
        this.boundOnClick = this.onClickHandler.bind(this);
        this.addEventListener("click", this.boundOnClick);

        // Initialize after DOM is ready
        if (document.readyState === "complete") {
          this.initObserver();
        } else {
          this.boundOnLoad = () => this.initObserver();
          window.addEventListener("load", this.boundOnLoad);
        }
      }

      /**
       * Handle anchor click events
       * @param {Event} event
       */
      onClickHandler(event) {
        const anchorItem = event.target.closest(".anchor-item");
        if (!anchorItem) return;

        event.preventDefault();

        const anchor = this.anchors.find(
          (anchor) => anchor.element === anchorItem,
        );
        if (anchor?.target) {
          const anchorOffset = window.innerHeight * 0.3;
          webvista.scrollToElementWithOffset(anchor.target, anchorOffset);
        }
      }

      /**
       * Collects all elements with the class "anchor-item" within the current context,
       * maps them to objects containing the element and its corresponding target element
       * (identified by the data-target-id attribute), and filters out anchors without a valid target.
       */
      getAnchors() {
        this.anchors = Array.from(this.querySelectorAll(".anchor-item"))
          .map((item) => {
            const targetId = item.dataset.targetId;
            let target = targetId ? document.getElementById(targetId) : null;

            // Stored anchor ids embed a template instance number
            // (shopify-section-template--<n>__<key>) that changes when a
            // theme is duplicated, orphaning every anchor. Fall back to the
            // stable section-key suffix.
            if (!target && targetId) {
              const key = targetId.split("__").pop();
              if (key) {
                target = document.querySelector(
                  '[id^="shopify-section-"][id$="__' + key + '"]',
                );
              }
            }

            // Hide items whose section is missing or disabled — a nav button
            // that silently does nothing reads as a broken page.
            if (!target) item.style.display = "none";

            return { element: item, target };
          })
          .filter((anchor) => anchor.target !== null);
      }

      /**
       * Initializes the Intersection Observer to track anchor targets' visibility.
       * Cleans up any previous observer before creating a new one.
       * Uses different observer options depending on whether the script is running inside an iframe.
       * When an anchor target becomes visible, sets the corresponding anchor as active.
       * Observes all anchor targets defined in `this.anchors`.
       */
      initObserver() {
        // First, clean up any previous observer
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }

        // Since rootMargin may not work inside iframes, use threshold instead
        const options = webvista.inIframe()
          ? { root: null, threshold: 0.3 }
          : { root: null, rootMargin: "-40% 0px" };

        this.intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Find which anchor corresponds to this target
              const activeAnchor = this.anchors.find(
                (anchor) => anchor.target === entry.target,
              );

              if (activeAnchor) {
                this.setAnchorActive(activeAnchor.element);
              }
            }
          });
        }, options);

        // Observe all anchor targets
        this.anchors.forEach((anchor) => {
          this.intersectionObserver.observe(anchor.target);
        });
      }

      disconnectedCallback() {
        if (this.intersectionObserver) this.intersectionObserver.disconnect();
        if (this.boundOnClick)
          this.removeEventListener("click", this.boundOnClick);
        if (this.boundOnLoad)
          window.removeEventListener("load", this.boundOnLoad);
      }

      /**
       * Set anchor active
       * @param {Element} item
       */
      setAnchorActive(item) {
        if (!item) return;

        this.querySelector(".active")?.classList.remove("active");
        item.classList.add("active");

        // Set underline position
        this.setLinePosition(item);

        // Send a custom "item-active" event
        // Useful for scrollable content views to capture and move the item into viewport
        this.dispatchEvent(
          new CustomEvent("item-active", {
            bubbles: true,
            detail: {
              element: item,
            },
          }),
        );
      }

      /**
       * Set underline position
       * @param {Element} item
       */
      setLinePosition(item) {
        if (!item) return;

        const wrapperRect = this.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();

        // Compensate for scroll offset within the scrollable container
        const scrollLeft = this.scrollLeft || 0;

        const left = itemRect.left - wrapperRect.left + scrollLeft;
        const right = wrapperRect.right - itemRect.right - scrollLeft;

        this.style.setProperty("--item-left", `${left}px`);
        this.style.setProperty("--item-right", `${right}px`);
      }
    },
  );
}
